import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HubLayout from '../../components/hub/HubLayout.jsx';

const PAGE_TO_ROUTE = {
  'index': '',
  'domains': '/domains',
  'study-plan': '/study-plan',
  'flashcards': '/flashcards',
  'practice-test': '/practice-test',
  'objective-tests': '/objective-tests',
  'cheat-sheet': '/cheat-sheet',
  'resources': '/resources',
};

/**
 * Renders the <main> content of a legacy HTML page inside the React HubLayout.
 * Used as a transitional bridge for static-heavy pages.
 *
 * Rewrites intra-hub `.html` anchor links to their React route equivalents,
 * and binds a click handler on `#print-sheet` (cheat-sheet print button).
 */
export default function LegacyContentPage({ config, page }) {
  const [html, setHtml] = useState('');
  const [err, setErr] = useState(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    fetch(`/legacy/${config.slug}/${page}.html`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (cancelled) return;
        const m = text.match(/<main[^>]*>([\s\S]*?)<\/main>/i);
        setHtml(m ? m[1] : text);
      })
      .catch((e) => !cancelled && setErr(e.message));
    return () => { cancelled = true; };
  }, [config.slug, page]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root || !html) return undefined;

    const anchors = root.querySelectorAll('a[href]');
    const handler = (e) => {
      const a = e.currentTarget;
      const href = a.getAttribute('href');
      if (!href) return;
      if (href === '../index.html' || href === '../') {
        e.preventDefault();
        navigate('/');
        return;
      }
      const m = href.match(/^([a-z-]+)\.html(?:#.*)?$/i);
      if (m) {
        const route = PAGE_TO_ROUTE[m[1].toLowerCase()];
        if (route !== undefined) {
          e.preventDefault();
          navigate(`/hub/${config.slug}${route}`);
        }
      }
    };
    anchors.forEach((a) => a.addEventListener('click', handler));

    const printBtn = root.querySelector('#print-sheet');
    const print = () => window.print();
    if (printBtn) printBtn.addEventListener('click', print);

    return () => {
      anchors.forEach((a) => a.removeEventListener('click', handler));
      if (printBtn) printBtn.removeEventListener('click', print);
    };
  }, [html, config.slug, navigate]);

  return (
    <HubLayout config={config}>
      {err ? (
        <section><div className="callout"><strong>LOAD ERROR</strong>Could not load page: {err}</div></section>
      ) : (
        <div ref={containerRef} dangerouslySetInnerHTML={{ __html: html }} />
      )}
    </HubLayout>
  );
}
